import {
  Accordion, Badge, Button, Card, Container, Group, Loader, Modal, Select,
  Stack, Table, Text, TextInput, Title, ThemeIcon,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconTemplate, IconPlus, IconTrash, IconUpload, IconTypography, IconDownload } from "@tabler/icons-react";
import { useState, useEffect, useRef } from "react";
import { getRendererInfo } from "../api";
import { jsonGet, jsonPost, jsonPut, jsonDelete, getAuthHeaders, getTenants } from "../../../Boilerplate/api";

// Sentinel select value for the global (all-tenants) scope. Empty string maps
// to null TenantId on the wire.
const GLOBAL_SCOPE = "";

// Direct API calls for admin template CRUD
const getGlobalTemplates = () => jsonGet("templates/all");
const createTemplate = (dto) => jsonPost("templates", dto);
const updateTemplate = (id, dto) => jsonPut(`templates/${id}`, dto);
const deleteTemplate = (id) => jsonDelete(`templates/${id}`);
const uploadTemplateFile = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const resp = await fetch(`/api/v1/templates/${id}/upload`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};
const downloadTemplateFile = async (id, fallbackName) => {
  const resp = await fetch(`/api/v1/templates/${id}/download`, { headers: getAuthHeaders() });
  if (!resp.ok) throw new Error(await resp.text());
  const blob = await resp.blob();
  // Prefer the server-supplied filename (carries the real extension); fall back to the name.
  const cd = resp.headers.get("Content-Disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const filename = match ? decodeURIComponent(match[1]) : `${fallbackName || "template"}.docx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Font API calls
const getGlobalFonts = () => jsonGet("fonts/all");
const createFont = (dto) => jsonPost("fonts", dto);
const deleteFont = (id) => jsonDelete(`fonts/${id}`);
const uploadFontFile = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const resp = await fetch(`/api/v1/fonts/${id}/upload`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};

export default function AdminTemplatesPage() {
  const [renderers, setRenderers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [fontCreateOpened, { open: openFontCreate, close: closeFontCreate }] = useDisclosure(false);

  const loadTemplates = () =>
    getGlobalTemplates().then((t) => setTemplates(t ?? []));

  const loadFonts = () =>
    getGlobalFonts().then((f) => setFonts(f ?? []));

  useEffect(() => {
    // Tenants drive the scope picker; tolerate a missing TenantsManage
    // permission so the templates page still loads without it.
    Promise.all([
      getRendererInfo(), getGlobalTemplates(), getGlobalFonts(),
      getTenants(1, 200).catch(() => ({ data: [] })),
    ])
      .then(([rend, tpls, fnts, tnts]) => {
        setRenderers(rend ?? []);
        setTemplates(tpls ?? []);
        setFonts(fnts ?? []);
        setTenants(tnts?.data ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Scope <Select> options: Global first, then each tenant by name.
  const scopeOptions = [
    { value: GLOBAL_SCOPE, label: "Global (all tenants)" },
    ...tenants.map((t) => ({ value: t.id, label: t.name })),
  ];
  async function handleRescope(template, nextScope) {
    const tenantId = nextScope || null;
    try {
      const updated = await updateTemplate(template.id, { updateScope: true, tenantId });
      setTemplates((prev) => prev.map((t) => (t.id === template.id ? updated : t)));
      notifications.show({ title: "Scope updated", message: "Template scope changed", color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to change scope", color: "red" });
    }
  }

  async function handleDelete(id) {
    try {
      await deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      notifications.show({ title: "Deleted", message: "Template removed", color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to delete template", color: "red" });
    }
  }

  async function handleDeleteFont(id) {
    try {
      await deleteFont(id);
      setFonts((prev) => prev.filter((f) => f.id !== id));
      notifications.show({ title: "Deleted", message: "Font removed", color: "green" });
    } catch {
      notifications.show({ title: "Error", message: "Failed to delete font", color: "red" });
    }
  }

  if (loading) return <Loader />;

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="lg">Document Templates</Title>

      {/* Configured templates */}
      <Card mb="lg">
        <Group justify="space-between" mb="md">
          <Title order={5}>Configured Templates</Title>
          <Button leftSection={<IconPlus size={16} />} size="sm" onClick={openCreate}>
            Add Template
          </Button>
        </Group>

        {templates.length === 0 ? (
          <Text size="sm" c="dimmed">No templates configured yet. Add one to get started.</Text>
        ) : (
          <ResponsiveTable minWidth={680}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Purpose</Table.Th>
                <Table.Th>Scope</Table.Th>
                <Table.Th>File</Table.Th>
                <Table.Th>Default</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {templates.map((t) => (
                <Table.Tr key={t.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{t.name}</Text>
                  </Table.Td>
                  <Table.Td><Badge size="sm" variant="light">{t.purpose}</Badge></Table.Td>
                  <Table.Td>
                    <Select
                      size="xs" w={180} comboboxProps={{ withinPortal: true }}
                      data={scopeOptions}
                      value={t.tenantId ?? GLOBAL_SCOPE}
                      onChange={(v) => handleRescope(t, v)}
                      // Standard (seeded) templates stay global — only customized
                      // templates can be scoped to a tenant.
                      disabled={t.isDefault}
                    />
                  </Table.Td>
                  <Table.Td>
                    {t.hasFile ? (
                      <Group gap="xs">
                        <Badge size="xs" color="green">Uploaded</Badge>
                        {/* Typst templates are built-in (no uploaded file to fetch). */}
                        {t.format !== "typst" && (
                          <DownloadButton templateId={t.id} name={t.name} />
                        )}
                      </Group>
                    ) : (
                      <UploadButton templateId={t.id} onUploaded={loadTemplates} />
                    )}
                  </Table.Td>
                  <Table.Td>
                    {t.isDefault && <Badge size="xs" color="blue">Default</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle" color="red" size="xs" p={4}
                      onClick={() => handleDelete(t.id)}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </ResponsiveTable>
        )}
      </Card>

      {/* Custom fonts */}
      <Card mb="lg">
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon variant="light" size="sm">
              <IconTypography size={14} />
            </ThemeIcon>
            <Title order={5}>Custom Fonts</Title>
          </Group>
          <Button leftSection={<IconPlus size={16} />} size="sm" onClick={openFontCreate}>
            Add Font
          </Button>
        </Group>

        <Text size="sm" c="dimmed" mb="md">
          Upload custom fonts (.ttf, .otf) to use in generated documents. Fonts are available to all templates.
        </Text>

        {fonts.length === 0 ? (
          <Text size="sm" c="dimmed">No custom fonts uploaded yet.</Text>
        ) : (
          <ResponsiveTable minWidth={680}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Font Family</Table.Th>
                <Table.Th>Style</Table.Th>
                <Table.Th>File</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fonts.map((f) => (
                <Table.Tr key={f.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>{f.name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{f.fontFamily}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light">{f.style || "Regular"}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {f.hasFile
                      ? <Badge size="xs" color="green">
                          {f.fileSizeBytes > 0 ? `${Math.round(f.fileSizeBytes / 1024)} KB` : "Uploaded"}
                        </Badge>
                      : <FontUploadButton fontId={f.id} onUploaded={loadFonts} />}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle" color="red" size="xs" p={4}
                      onClick={() => handleDeleteFont(f.id)}
                    >
                      <IconTrash size={14} />
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </ResponsiveTable>
        )}
      </Card>

      {/* Renderer data reference */}
      <Card>
        <Title order={5} mb="xs">Template Data Reference</Title>
        <Text size="sm" c="dimmed" mb="md">
          Each template purpose has a specific renderer that injects candidate data into the DOCX.
          Use this reference when designing templates.
        </Text>
        <Accordion variant="separated">
          {renderers.map((r) => (
            <Accordion.Item key={r.purpose} value={r.purpose}>
              <Accordion.Control>
                <Group gap="sm">
                  <ThemeIcon variant="light" size="sm">
                    <IconTemplate size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={500}>{r.name}</Text>
                    <Text size="xs" c="dimmed">{r.purpose}</Text>
                  </div>
                  {r.candidateFields.length === 0 && (
                    <Badge size="xs" variant="light" color="gray">Not implemented</Badge>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Text size="sm" mb="md">{r.description}</Text>
                {r.projectFields.length > 0 && (
                  <>
                    <Text size="xs" fw={600} mt="sm" mb="xs" c="dimmed" tt="uppercase">Project context</Text>
                    <FieldTable fields={r.projectFields} />
                  </>
                )}
                {r.candidateFields.length > 0 && (
                  <>
                    <Text size="xs" fw={600} mt="md" mb="xs" c="dimmed" tt="uppercase">Per candidate</Text>
                    <FieldTable fields={r.candidateFields} />
                  </>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Card>

      <CreateTemplateModal
        opened={createOpened}
        onClose={closeCreate}
        renderers={renderers}
        scopeOptions={scopeOptions}
        onCreated={() => { closeCreate(); loadTemplates(); }}
      />

      <CreateFontModal
        opened={fontCreateOpened}
        onClose={closeFontCreate}
        onCreated={() => { closeFontCreate(); loadFonts(); }}
      />
    </Container>
  );
}

function FieldTable({ fields }) {
  return (
    <ResponsiveTable minWidth={680}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Field</Table.Th>
          <Table.Th>Description</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {fields.map((f) => (
          <Table.Tr key={f.name}>
            <Table.Td>
              <Text size="sm" ff="monospace" fw={500}>{f.name}</Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{f.description}</Text>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </ResponsiveTable>
  );
}

function UploadButton({ templateId, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadTemplateFile(templateId, file);
      notifications.show({ title: "Uploaded", message: "Template file uploaded", color: "green" });
      onUploaded();
    } catch {
      notifications.show({ title: "Error", message: "Upload failed", color: "red" });
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <input type="file" ref={fileRef} style={{ display: "none" }} accept=".docx,.pptx" onChange={handleFile} />
      <Button
        variant="light" size="xs" leftSection={<IconUpload size={12} />}
        loading={uploading}
        onClick={() => fileRef.current?.click()}
      >
        Upload
      </Button>
    </>
  );
}

function DownloadButton({ templateId, name }) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadTemplateFile(templateId, name);
    } catch {
      notifications.show({ title: "Error", message: "Download failed", color: "red" });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Button
      variant="subtle" size="xs" p={4} leftSection={<IconDownload size={12} />}
      loading={downloading}
      onClick={handleDownload}
    >
      Download
    </Button>
  );
}

function FontUploadButton({ fontId, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadFontFile(fontId, file);
      notifications.show({ title: "Uploaded", message: "Font file uploaded", color: "green" });
      onUploaded();
    } catch (err) {
      notifications.show({ title: "Error", message: err.message || "Upload failed", color: "red" });
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <input type="file" ref={fileRef} style={{ display: "none" }} accept=".ttf,.otf" onChange={handleFile} />
      <Button
        variant="light" size="xs" leftSection={<IconUpload size={12} />}
        loading={uploading}
        onClick={() => fileRef.current?.click()}
      >
        Upload
      </Button>
    </>
  );
}

function CreateTemplateModal({ opened, onClose, renderers, scopeOptions, onCreated }) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [scope, setScope] = useState(GLOBAL_SCOPE);
  const [saving, setSaving] = useState(false);

  const purposeOptions = renderers
    .filter((r) => r.candidateFields.length > 0)
    .map((r) => ({ value: r.purpose, label: r.name }));

  const selectedRenderer = renderers.find((r) => r.purpose === purpose);

  async function handleSubmit() {
    if (!name || !purpose) return;
    setSaving(true);
    try {
      await createTemplate({ tenantId: scope || null, name, purpose, isDefault: false });
      notifications.show({ title: "Created", message: "Template created — now upload a DOCX file", color: "green" });
      setName("");
      setPurpose("");
      setScope(GLOBAL_SCOPE);
      onCreated();
    } catch {
      notifications.show({ title: "Error", message: "Failed to create template", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add Template">
      <Stack>
        <TextInput label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Purpose"
          required
          data={purposeOptions}
          value={purpose}
          onChange={setPurpose}
        />
        {selectedRenderer && (
          <Text size="xs" c="dimmed">
            {selectedRenderer.description}
          </Text>
        )}
        <Select
          label="Scope"
          description="Global templates are available to every tenant. Pick a tenant for a customized, tenant-only template."
          data={scopeOptions}
          value={scope}
          onChange={(v) => setScope(v ?? GLOBAL_SCOPE)}
        />
        <Button onClick={handleSubmit} loading={saving} disabled={!name || !purpose}>
          Create
        </Button>
      </Stack>
    </Modal>
  );
}

function CreateFontModal({ opened, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [fontFamily, setFontFamily] = useState("");
  const [style, setStyle] = useState("Regular");
  const [saving, setSaving] = useState(false);

  async function handleSubmit() {
    if (!name || !fontFamily) return;
    setSaving(true);
    try {
      await createFont({ tenantId: null, name, fontFamily, style });
      notifications.show({ title: "Created", message: "Font created — now upload the font file", color: "green" });
      setName("");
      setFontFamily("");
      setStyle("Regular");
      onCreated();
    } catch {
      notifications.show({ title: "Error", message: "Failed to create font", color: "red" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add Font">
      <Stack>
        <TextInput
          label="Display Name"
          placeholder="e.g. Montserrat Bold"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextInput
          label="Font Family"
          placeholder="e.g. Montserrat"
          required
          value={fontFamily}
          onChange={(e) => setFontFamily(e.target.value)}
        />
        <Select
          label="Style"
          data={[
            { value: "Regular", label: "Regular" },
            { value: "Bold", label: "Bold" },
            { value: "Italic", label: "Italic" },
            { value: "BoldItalic", label: "Bold Italic" },
          ]}
          value={style}
          onChange={setStyle}
        />
        <Button onClick={handleSubmit} loading={saving} disabled={!name || !fontFamily}>
          Create
        </Button>
      </Stack>
    </Modal>
  );
}
